// src/components/payment/PaymentSuccess.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

export const PaymentSuccess: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [autoCloseTimer, setAutoCloseTimer] = useState(30);

  // Get data from URL parameters
  const paidAmount = parseFloat(searchParams.get('amount') || '0');
  const remainingDebt = parseFloat(searchParams.get('remaining') || '0');
  const timestamp = searchParams.get('timestamp');

  useEffect(() => {
    // Auto-close countdown
    const timer = setInterval(() => {
      setAutoCloseTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (window.confirm('هل تريد إغلاق نافذة تأكيد الدفع؟')) {
            window.close();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3
    }).format(amount);
  };

  const formatDateTime = (timestamp: string | null) => {
    if (!timestamp) return new Date().toLocaleString('ar-KW');
    
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleString('ar-KW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Success Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 text-center">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold">تم الدفع بنجاح!</h1>
            <p className="text-green-100 mt-2">تمت معالجة دفعة مديونية الائتمان</p>
          </div>

          <div className="p-6">
            {/* Payment Amount */}
            <div className="bg-green-50 rounded-xl p-6 mb-6 text-center">
              <p className="text-green-700 mb-2 font-medium">المبلغ المدفوع</p>
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(paidAmount)}
              </p>
              <p className="text-sm text-green-600 mt-2">
                تم الدفع في {formatDateTime(timestamp)}
              </p>
            </div>

            {/* Account Status */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                حالة الائتمان
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">المديونية المتبقية:</span>
                  <p className="font-medium">
                    {remainingDebt > 0 ? (
                      <span className="text-amber-600">{formatCurrency(remainingDebt)}</span>
                    ) : (
                      <span className="text-green-600">0.000 د.ك ✓</span>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-gray-600">حالة الحساب:</span>
                  <p className="font-medium">
                    {remainingDebt > 0 ? (
                      <span className="inline-block bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs">
                        دفعة جزئية
                      </span>
                    ) : (
                      <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                        تم تسوية المديونية
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Status Message */}
            {remainingDebt > 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-amber-600 ml-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-amber-800">ملاحظة مهمة</p>
                    <p className="text-sm text-amber-700 mt-1">
                      لا يزال لديك مديونية قدرها {formatCurrency(remainingDebt)}. 
                      سيبقى حسابك مقيداً حتى يتم تسوية المبلغ كاملاً.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 ml-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-medium text-green-800">مبروك!</p>
                    <p className="text-sm text-green-700 mt-1">
                      تم تسوية مديونية الائتمان بالكامل. حسابك الآن في وضع جيد ويمكنك إجراء عمليات شراء جديدة.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Next Steps */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                ما يحدث الآن؟
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                {remainingDebt <= 0 ? (
                  <>
                    <li>• تم تحديث حالة حسابك إلى "وضع جيد"</li>
                    <li>• يمكنك الآن إجراء عمليات شراء مهام جديدة</li>
                    <li>• حد الائتمان الخاص بك متاح للاستخدام</li>
                  </>
                ) : (
                  <>
                    <li>• تم إضافة هذه الدفعة إلى حسابك</li>
                    <li>• تحتاج لدفع المبلغ المتبقي {formatCurrency(remainingDebt)}</li>
                    <li>• عمليات الشراء الجديدة مقيدة حتى تسوية المديونية</li>
                  </>
                )}
                <li>• ستتلقى تأكيد الدفع عبر واتساب/رسالة نصية</li>
                <li>• تم إرسال إيصال إلى بريدك الإلكتروني المسجل</li>
              </ul>
            </div>

            {/* Receipt Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <svg className="w-5 h-5 text-gray-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium text-gray-800">احتفظ بهذا التأكيد للمراجعة</span>
              </div>
              <p className="text-sm text-gray-600">
                تمت معالجة العملية بأمان عبر MyFatoorah
                <br />
                تاريخ الدفع: {formatDateTime(timestamp)}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => window.print()}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                طباعة التأكيد
              </button>
              
              <button
                onClick={() => window.close()}
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-gray-700 transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                إغلاق النافذة ({autoCloseTimer}s)
              </button>
            </div>

            {/* Support Info */}
            <div className="text-center mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                أسئلة حول دفعتك؟
                <br />
                <span className="font-medium text-blue-600">📞 الدعم: +965-56501847</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};